import { useQuery } from '@tanstack/react-query';
import { isArray } from 'lodash';
import { z } from 'zod';
import { useEnabledQuery } from '../../ui/hooks/useEnabledQuery';
import { getFormattedPropertyValidation } from '../../ui/util/utilFunctions';
import { ResourceType } from '../enumerations/ResourceType';
import { parseRawData, useOnUnauthorized } from '../resource-base/adapters/AdapterUtils';
import { get } from '../resource-base/APIVersion1';
import {
    PropertyDetailsPayload,
    PropertyDetailsPayloadSchema,
    PropertyDetailsResource,
} from '../resources/propertyDetailsResource';
import {
    ValidationEntryDetailsPayload,
    ValidationEntryDetailsPayloadSchema,
} from '../resources/validation/validationEntryDetailsResource';
import { PropertyValidation } from './outlineTypes';
import { getUseActionsByResourceType } from '../resource-base/getUseActionsByResourceType';

export function useSystemInformationActions() {
    const onUnauthorized = useOnUnauthorized();
    const getEnabled = useEnabledQuery();

    const getValidationEntryDetails = (group: string): Promise<ValidationEntryDetailsPayload | null> => {
        return get(`information/validation/entry?group=${group}`).then((result) => {
            if (result.status === 200) {
                return result.json().then((rawData) => parseRawData(rawData, ValidationEntryDetailsPayloadSchema));
            } else if (result.status === 401) {
                return onUnauthorized(result, null);
            }
            throw Error(`Validation Entry Details could not be found`);
        });
    };

    const getPropertyDetails = (group: string): Promise<PropertyDetailsPayload | null> => {
        return get(`information/property/entry?group=${group}`).then((result) => {
            if (result.status === 200) {
                return result.json().then((rawData) => parseRawData(rawData, PropertyDetailsPayloadSchema));
            } else if (result.status === 401) {
                return onUnauthorized(result, null);
            }
            throw Error(`Property Details could not be found`);
        });
    };

    const getResourceSubTypes = (group: string): Promise<string[] | null> => {
        return get(`information/sub-types?group=${group}`).then((result) => {
            if (result.status === 200) {
                return result.json().then((rawData) => parseRawData(rawData, z.string().array()));
            } else if (result.status === 401) {
                return onUnauthorized(result, null);
            }
            throw Error(`Resource SubTypes could not be found`);
        });
    };

    const useGetValidationEntryDetails = (group: string) => {
        return useQuery({
            queryKey: ['INFINITY_STALE_TIME', 'VALIDATION_ENTRY_DETAILS', group],
            queryFn: () => getValidationEntryDetails(group),

            enabled: getEnabled(group),
            refetchInterval: false,
            staleTime: Infinity,
        });
    };

    const useGetPropertyDetailsInternal = (group: string) => {
        return useQuery({
            queryKey: ['INFINITY_STALE_TIME', 'PROPERTY_DETAILS', group],
            queryFn: () => getPropertyDetails(group),
            enabled: getEnabled(group),
            refetchInterval: false,
            staleTime: Infinity,
        });
    };

    const useGetSpecificPropertyDetails = (property: string | Array<string>, group: string) => {
        const propertyDetails = useGetPropertyDetailsInternal(group);
        if (Array.isArray(property)) {
            return property
                .map((value) => propertyDetails.data?.propertyDetails[value])
                .filter((value) => value !== undefined) as Array<PropertyDetailsResource>;
        }
        return propertyDetails.data?.propertyDetails[property];
    };

    const useGetResourceSubTypes = (group: string) => {
        return useQuery({
            queryKey: ['INFINITY_STALE_TIME', 'SUB_TYPES', group],
            queryFn: () => getResourceSubTypes(group),
            enabled: getEnabled(group),
            refetchInterval: false,
            staleTime: Infinity,
        });
    };

    const useGetPropertyValidationWithId = (
        property: string | string[],
        resourceType: ResourceType,
        resourceId: string | undefined | null,
    ): PropertyValidation | null => {
        const validationResult = getUseActionsByResourceType(resourceType)().useValidateResource(resourceId);
        const validationDetails = useGetValidationEntryDetails(resourceType);
        const propertyDetail = { ...validationDetails.data?.property, ...validationDetails.data?.compound };
        return getFormattedPropertyValidation(
            property,
            validationResult.data?.propertyValidationMapping,
            propertyDetail,
        );
    };

    const useGetPropertyDetails = (property: string | string[], resourceType: ResourceType) => {
        const propertyDetails = useGetPropertyDetailsInternal(resourceType);
        if (isArray(property)) {
            return (property as string[])
                .map((value) => propertyDetails.data?.propertyDetails[value])
                .filter((value) => value !== undefined) as PropertyDetailsResource[];
        }
        return propertyDetails.data?.propertyDetails[property as string];
    };

    const useGetAllPropertyDetails = (resourceType: ResourceType) => {
        const propertyDetails = useGetPropertyDetailsInternal(resourceType);
        return propertyDetails.data?.propertyDetails;
    };

    return {
        useGetValidationEntryDetails,
        useGetPropertyDetails,
        useGetSpecificPropertyDetails,
        useGetResourceSubTypes,
        useGetPropertyValidationWithId,
        useGetAllPropertyDetails,
    };
}
